import { useMemo, type ComponentProps, type RefObject } from 'react';

import { useFlags } from '@mint-lib/routing-context';
import { Card } from '@mint-lib/tw-components';
import type { AvatarAi } from '@mint-lib/tw-components/ai';
import { cn } from '@mint-lib/tw-utils';
import { Chat } from '@mint-sdk/api-ai-assistant';

import { buildMessageGraph } from '../../../features/MessageConstructor/helpers.js';
import {
  MessageContent,
  ReportBlock,
} from '../../../features/MessageConstructor/index.js';
import { MessageConstructor } from '../../../features/MessageConstructor/MessageConstructor.jsx';
import { ReasoningSummaryAccordion } from '../../../features/reasoningSummary/index.js';
import { resolveReportDownload } from '../../../helpers/resolveReportDownload.js';
import { ReasoningStep } from '../../../types/reasoningSteps.js';
import { AgentLabel } from './AgentLabel.jsx';

type Props = {
  text: string;
  date: string;
  chatId: string;
  messageId?: number;
  additionalMetadata?: Chat.AiChatMessageRecordType['message']['additionalMetadata'];
  isSystemError?: boolean;
  // Hide extra content (tables, charts) if chat window is small
  shouldBeVisibleExtraContent?: boolean;
  reasoningData?: ReasoningStep['data'];
  webSources?: string[] | null;
  knowledgeBaseSources?: string[] | null;
  isFullscreen?: boolean;
  avatarSize?: ComponentProps<typeof AvatarAi>['size'];
  isInterrupted?: boolean;
  isLast?: boolean;
  containerRef?: RefObject<HTMLDivElement | null>;
};

const AiMessage = ({
  text,
  date,
  chatId,
  isSystemError,
  additionalMetadata,
  messageId,
  reasoningData,
  webSources,
  knowledgeBaseSources,
  shouldBeVisibleExtraContent = true,
  isFullscreen = false,
  avatarSize,
  isInterrupted,
  isLast,
  containerRef,
}: Props) => {
  const ff = useFlags('@mint/arm', ['aiMultipleTables']);
  const { dataframe, tables, metadata, agentName, graphs } =
    additionalMetadata || {};
  const messageGraph = useMemo(
    () => buildMessageGraph(text, { graphs }),
    [text, graphs],
  );
  const hasTagsInText =
    ff.aiMultipleTables.enabled && messageGraph.blocksOrder.length > 1;

  const report = resolveReportDownload(metadata);

  if (isLast && isInterrupted) return null;

  return (
    <div data-testid={`ai-message-${messageId}`}>
      <Card
        variant="outlined"
        className={cn({
          'bg-background-error': isSystemError,
          'w-full': isFullscreen,
        })}
        contentClassName="flex flex-col gap-5"
      >
        {hasTagsInText ? (
          <MessageConstructor
            type={'agentic'}
            messageGraph={messageGraph}
            isSystemError={isSystemError}
            date={date}
            isFullscreen={isFullscreen}
            chatId={chatId}
            messageId={String(messageId)}
            graphs={additionalMetadata?.graphs}
            tables={additionalMetadata?.tables}
            containerRef={containerRef}
          />
        ) : (
          <MessageContent
            contentVisible={shouldBeVisibleExtraContent}
            type={'agentic'}
            text={text}
            isSystemError={isSystemError}
            date={date}
            isFullscreen={isFullscreen}
            chatId={chatId}
            messageId={String(messageId)}
            dataframe={
              Object.values(tables ?? {})?.[0] ?? dataframe // Enables old contract support
            }
            graph={
              Object.values(graphs ?? {})?.[0] ??
              (metadata?.graph as Chat.Graph) // Enables old contract support
            }
          />
        )}

        {shouldBeVisibleExtraContent && report && (
          <ReportBlock report={report} chatId={chatId} />
        )}

        <ReasoningSummaryAccordion
          reasoningData={reasoningData || []}
          webSources={webSources || []}
          knowledgeBaseSources={knowledgeBaseSources || []}
        />
      </Card>

      {agentName && (
        <div className="mt-2">
          <AgentLabel
            labelDirection="column"
            agentName={agentName}
            avatarSize={avatarSize}
          />
        </div>
      )}
    </div>
  );
};

export { AiMessage };
